import type {
  StepperProps as MantineStepperProps,
  StepperStepProps as MantineStepperStepProps,
} from '@mantine/core';
import { Stepper as MantineStepper } from '@mantine/core';

import type { LockedProps } from '../types';

export type StepperProps = Omit<MantineStepperProps, LockedProps | 'variant'> & {
  /**
   * `headless` hides the bar and separators (see the theme's `Stepper` entry), keeping only the
   * step-content switching. Mantine declares no variants of its own for Stepper, so this is the
   * whole set.
   */
  variant?: 'headless';
};

export type StepperStepProps = Omit<MantineStepperStepProps, LockedProps>;

const StepperStep = (props: StepperStepProps) => {
  return <MantineStepper.Step {...props} />;
};

const StepperRoot = (props: StepperProps) => {
  return <MantineStepper {...props} />;
};

type StepperRootComponent = typeof StepperRoot;

interface StepperCompound extends StepperRootComponent {
  /**
   * One step: the marker in the bar, plus the content rendered while it's active. Mantine injects
   * `step`, `state` and the click plumbing — pass only `label`, `description` and children.
   */
  Step: typeof StepperStep;
  /**
   * Content shown once `active` passes the last step. This is Mantine's own component, not a
   * locked wrapper: Stepper picks it out of its children by strict identity
   * (`child.type !== StepperCompleted`), so anything wrapping it would be treated as one more step.
   */
  Completed: typeof MantineStepper.Completed;
}

/**
 * ### Stepper
 *
 * @description
 * Mantine's Stepper minus per-call-site style overrides (see {@link LockedProps}). Renders a
 * numbered bar of `Stepper.Step`s and the active step's content, with `Stepper.Completed` for the
 * end state. Drive it with `active`; add `onStepClick` to let users jump back.
 *
 * `variant="headless"` hides the bar and separators (see the theme's `Stepper` entry) for flows
 * that show progress some other way — a `Progress` bar above the form, say — but still want the
 * step-content switching.
 *
 * @example
 * ```tsx
 * <Stepper active={step} onStepClick={setStep}>
 *   <Stepper.Step label="Phone">
 *     <PhoneForm />
 *   </Stepper.Step>
 *   <Stepper.Step label="Address">
 *     <AddressForm />
 *   </Stepper.Step>
 *   <Stepper.Completed>All set.</Stepper.Completed>
 * </Stepper>
 * ```
 *
 * @param props - {@link StepperProps}
 * @returns {@link StepperCompound}
 */
export const Stepper: StepperCompound = Object.assign(StepperRoot, {
  Step: StepperStep,
  Completed: MantineStepper.Completed,
});
